import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import { Button, Row, Col, Typography, message, Card } from 'antd';
import HKServiceTable from '@components/HKServiceTable';
import {
  TextInput,
  MultiSelect,
  RadioGroup,
  TextArea,
  DatePickerInput
} from '@components/form';
import { RegionField } from '@containers/form';

import UsersActions, { UsersSelectors } from '@redux/UsersRedux';
import validationSchema from './schema';

import API from '@utils/api';
import { filterOutList } from '@utils/filters';
import { getErrors } from '@utils/error';
import history from '@utils/history';
import { isValidDate } from '@utils/date';
import { DAYS } from '@services/transform';
import moment from 'moment';

const { Title } = Typography;

function transfromToFormValue(data) {
  const { ...resetFields } = data;
  if (resetFields.activeAt) {
    resetFields.activeAt = moment(resetFields.activeAt, 'YYYY-MM-DD');
  }
  const newObj = {};
  Object.keys(resetFields).forEach(objKey => {
    if (resetFields[objKey]) {
      newObj[objKey] = resetFields[objKey];
    }
  });
  return newObj;
}

function transformRequest(formValues) {
  const {
    regionID,
    internalNotes,
    activeAt,
    status,
    role,
    ...restValues
  } = formValues;

  const newObj = {
    role,
    ...restValues
  };

  if (role === 'house_keeper') {
    newObj.status = status;
    newObj.regionID = regionID;
    newObj.activeAt = isValidDate(activeAt) ? activeAt : undefined;
  } else if (role === 'property_manager') {
    newObj.internalNotes = internalNotes;
  }
  return newObj;
}

const INITIAL_HK_AVAILABILITIES = [
  {
    availabilityID: 858,
    day: DAYS.monday,
    startHour: 9,
    endHour: 17
  },
  {
    availabilityID: 859,
    day: DAYS.tuesday,
    startHour: 9,
    endHour: 17
  },
  {
    availabilityID: 860,
    day: DAYS.wednesday,
    startHour: 9,
    endHour: 17
  },
  {
    availabilityID: 861,
    day: DAYS.thursday,
    startHour: 9,
    endHour: 17
  },
  {
    availabilityID: 862,
    day: DAYS.friday,
    startHour: 9,
    endHour: 17
  },
  {
    availabilityID: 863,
    day: DAYS.saturday,
    startHour: 9,
    endHour: 17
  }
];

class UserForm extends Component {
  static propTypes = {
    addUser: PropTypes.func,
    updateList: PropTypes.func,
    usersList: PropTypes.array,
    userData: PropTypes.object,
    userID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  };

  constructor(props) {
    super(props);
    this.state = {
      availabiliies: INITIAL_HK_AVAILABILITIES
    };
  }

  handleSubmit = async (values, actions) => {
    const { usersList, updateList, addUser, userData } = this.props;
    const { setSubmitting } = actions;
    const { userID, role, status } = values;
    setSubmitting(true);
    try {
      const transformedValues = transformRequest(values);
      if (!userID) {
        let { result } = await API.createUser(transformedValues);
        addUser(result);
        updateList(filterOutList([...usersList, result]));

        const { userID: newUserID } = result;
        const hkAvailabilities = this.state.availabiliies.map(item => {
          // eslint-disable-next-line no-unused-vars
          const { availabilityID, ...restItem } = item;
          return restItem;
        });

        await API.updateHkAvailability(newUserID, hkAvailabilities);
        setSubmitting(false);
        history.replace(`/users/${newUserID}`);
      } else {
        if (role == 'house_keeper') {
          const { status: initialStatus } = userData;
          if (status == 'active' && initialStatus != 'active') {
            await API.activateHk(userID, role);
          } else if (status == 'deactivated') {
            await API.updateActiveAt(userID, transformedValues);
            if (initialStatus != 'deactivated') {
              await API.deactivateHk(userID, role);
            }
          }
        }
        setSubmitting(false);
        history.push(`/users/${userID}`);
        // let { result } = await API.updateUser(userID, transformedValues);
      }
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
    }
    setSubmitting(false);
  };

  handleUpdateAvailabilities = async (row, editingKey) => {
    const newAvailabilities = this.state.availabiliies.map(item => {
      const { availabilityID } = item;
      if (availabilityID === editingKey) {
        return {
          ...item,
          ...row
        };
      }

      return item;
    });

    this.setState({
      availabiliies: newAvailabilities
    });
  };

  renderForm = props => {
    const { isValid, isSubmitting, values } = props;
    const { role, userID, status } = values;

    const isHouseKeeperInfoHidden = !role || role !== 'house_keeper';
    const isNotesHidden = !role || role !== 'property_manager';
    const showActiveAt =
      !isHouseKeeperInfoHidden && status && status !== 'active';

    return (
      <Form className="create-form ant-form-vertical">
        <Row display="flex">
          <Title level={3}>{userID ? 'Update User' : 'Create User'}</Title>
          <fieldset disabled={!!userID}>
            <Row justify="space-between" gutter={16}>
              <Col span={4}>
                <Field
                  component={TextInput}
                  mb={4}
                  name="firstName"
                  type="text"
                  label="First Name"
                />
              </Col>
              <Col span={4}>
                <Field
                  component={TextInput}
                  mb={4}
                  name="lastName"
                  type="text"
                  label="Last Name"
                />
              </Col>
              <Col span={4}>
                <Field
                  component={TextInput}
                  name="email"
                  type="text"
                  label="Email"
                />
              </Col>
              <Col span={4}>
                <Field
                  component={TextInput}
                  mb={4}
                  name="phone"
                  type="text"
                  label="Phone Number"
                />
              </Col>
            </Row>
          </fieldset>
          <Row justify="space-between" gutter={16}>
            <Col span={4}>
              <Field
                component={MultiSelect}
                mb={4}
                name="role"
                disabled={!!userID}
                options={[
                  { label: 'House Keeper', value: 'house_keeper' },
                  { label: 'Property Manager', value: 'property_manager' },
                  { label: 'Admin', value: 'admin' }
                ]}
                type="select"
                label="Role"
              />
            </Col>
            {!isHouseKeeperInfoHidden && !userID && (
              <Col span={4}>
                <Field
                  component={RegionField}
                  name="regionID"
                  label="Region"
                  mode="single"
                  showSearch
                />
              </Col>
            )}
            {!isHouseKeeperInfoHidden && (
              <Col span={4}>
                <Field
                  component={RadioGroup}
                  name="status"
                  label="Status"
                  options={[
                    { label: 'Inactive', value: 'deactivated' },
                    { label: 'Active', value: 'active' }
                  ]}
                />
              </Col>
            )}
            {showActiveAt && (
              <Col span={6}>
                <Field
                  component={DatePickerInput}
                  mb={4}
                  name="activeAt"
                  type="date"
                  label="Active At"
                />
              </Col>
            )}
          </Row>
          {!isNotesHidden && (
            <Row justify="space-between" gutter={16}>
              <Col span={8}>
                <Field
                  component={TextArea}
                  name="internalNotes"
                  type="text"
                  label="Notes"
                />
              </Col>
            </Row>
          )}
          {!isHouseKeeperInfoHidden && !userID && (
            <Row justify="space-between" gutter={16}>
              <Col span={16}>
                <Card title="Availability">
                  <HKServiceTable
                    availabiliies={this.state.availabiliies}
                    saveRowResolver={this.handleUpdateAvailabilities}
                  />
                </Card>
              </Col>
            </Row>
          )}
          <Row>
            <Col offset={9} span={6}>
              <Button
                flex={1}
                mb={4}
                type="primary"
                htmlType="submit"
                className="btn-submit"
                disabled={!isValid || isSubmitting}
              >
                {userID ? 'Update User' : 'Create User'}
              </Button>
            </Col>
          </Row>
        </Row>
      </Form>
    );
  };

  render() {
    const { userData } = this.props;

    const initialValues = userData
      ? transfromToFormValue(userData)
      : {
          firstName: '',
          lastName: '',
          email: '',
          role: '',
          phone: ''
        };

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
        render={this.renderForm}
        isInitialValid={validationSchema.isValidSync(initialValues)}
      />
    );
  }
}

const mapStateToProps = state => ({
  usersList: UsersSelectors.selectUsersList(state)
});
const mapDispatchToProps = dispatch => ({
  addUser: user => dispatch(UsersActions.addUser(user)),
  updateList: list => dispatch(UsersActions.updateList(list))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserForm);
